import {
  QueryKey,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { api, API_VERSION_TWO } from 'apis/axiosInstance'
import { AxiosError } from 'axios'
import { Toastr } from 'components/global/Toastr'
import { OvertimePolicy } from 'redux/setting/attendanceManagement/overtimePolicies/overtimePoliciesTypes'
import { queryClient } from 'hooks/useReactQuery'

import { HTTPValidationError } from 'types/httpTypes'
import {
  CompensationType,
  OvertimeRequestAxiosResponseT,
  OvertimeRequestT,
  OvertimePolicyPendingRequestAxiosResponseT,
  ExpireRequestParamsT,
  LeaveRequestAxiosResponseT,
} from './useOvertimeRequest.type'

// ? Query keys

export const getOvertimeRequestNewQueryKey = (
  employee_id: number,
  compensation: CompensationType,
  request_id?: number,
): QueryKey => ['overtime_requests_new', employee_id, compensation, request_id]

export const getOvertimePendingRequestQueryKey = (overtime_policy_id?: number): QueryKey => [
  'overtime_policies_pending_requests',
  overtime_policy_id,
]

// ? API

const getOvertimeRequestNew = (
  employee_id: number,
  params: { overtime_request: OvertimeRequestT; request_id?: number },
): Promise<OvertimeRequestAxiosResponseT> =>
  api({
    method: 'GET',
    url: `${API_VERSION_TWO}/attendance/employees/${employee_id}/overtime_requests/new`,
    params,
  })

const updateLeaveRequest = (params: ExpireRequestParamsT): Promise<LeaveRequestAxiosResponseT> => {
  return api({
    method: 'PUT',
    url: `${API_VERSION_TWO}/requests/expire`,
    params,
  })
}

const updateOvertimeRequestNew = (
  request_id: number,
  params: { approved_compensation: CompensationType },
): Promise<OvertimeRequestAxiosResponseT> => {
  return api({
    method: 'PUT',
    url: `${API_VERSION_TWO}/attendance/overtime_requests/${request_id}/update_compensation`,
    params,
  })
}
export const getOvertimePolicy = (
  overtime_policy_id: number,
): Promise<OvertimePolicyPendingRequestAxiosResponseT> => {
  return api({
    method: 'GET',
    url: `${API_VERSION_TWO}/overtime_policies/${overtime_policy_id}`,
  })
}
// ? API'S hooks

/**
 * @endpoint: GET: /v2/attendance/employees/${employee_id}/overtime_requests/new
 * @summary This hook used to get the compensation list
 */
export const useGetOvertimeRequestNew = <
  TData = OvertimeRequestAxiosResponseT,
  TError = HTTPValidationError,
>(
  employee_id: number,
  data: { overtime_request: OvertimeRequestT; request_id?: number },
  opts?: {
    query?: UseQueryOptions<OvertimeRequestAxiosResponseT, TError, TData> & {
      onErrorCallback?: () => void
    }
  },
): UseQueryResult<TData, TError> => {
  return useQuery({
    ...(opts?.query || {}),
    retry: 0,
    queryKey:
      opts?.query?.queryKey ??
      getOvertimeRequestNewQueryKey(
        employee_id,
        data.overtime_request.compensation,
        data?.request_id,
      ),
    queryFn: async () => {
      try {
        const res = await getOvertimeRequestNew(employee_id, data)
        return res
      } catch (error) {
        if (error instanceof AxiosError<HTTPValidationError>) {
          Toastr.error(error.response?.data.error)
          opts?.query?.onErrorCallback?.()
        }
        throw error
      }
    },
  })
}

/**
 * @endpoint: PUT: /v2/attendance/overtime_requests/${request_id}/update_compensation
 * @summary This hook used to update the compensation that already requested by employee
 */

export const useUpdateOvertimeRequestNew = <TError extends HTTPValidationError>(opts?: {
  mutation?: UseMutationOptions<
    OvertimeRequestAxiosResponseT,
    TError,
    { id: number; params: { approved_compensation: CompensationType } }
  >
}): UseMutationResult<
  OvertimeRequestAxiosResponseT,
  TError,
  { id: number; params: { approved_compensation: CompensationType } }
> => {
  return useMutation({
    ...(opts?.mutation || {}),
    mutationFn: (payload) => updateOvertimeRequestNew(payload.id, payload.params),
    onSuccess: (res, variables, context) => {
      opts?.mutation?.onSuccess?.(res, variables, context)
      Toastr.success(res.data.message)
    },
    onError: (error, variables, context) => {
      Toastr.error(error.response?.data.error)
      opts?.mutation?.onError?.(error, variables, context)
    },
  })
}

export const getFetchOvertimePolicy = (
  policy: OvertimePolicy,
): Promise<OvertimePolicyPendingRequestAxiosResponseT> =>
  queryClient.fetchQuery({
    queryKey: getOvertimePendingRequestQueryKey(policy.id),
    queryFn: () => getOvertimePolicy(policy.id),
    staleTime: 0,
  })

export const useUpdateLeaveRequest = <TError extends HTTPValidationError>(opts?: {
  mutation?: UseMutationOptions<
    LeaveRequestAxiosResponseT,
    TError,
    { params: ExpireRequestParamsT }
  >
}): UseMutationResult<LeaveRequestAxiosResponseT, TError, { params: ExpireRequestParamsT }> => {
  return useMutation({
    ...(opts?.mutation || {}),
    mutationFn: (payload) => updateLeaveRequest(payload.params),
    onSuccess: (res, variables, context) => {
      opts?.mutation?.onSuccess?.(res, variables, context)
      Toastr.success(res.data.message)
    },
    onError: (error, variables, context) => {
      Toastr.error(error.response?.data.error)
      opts?.mutation?.onError?.(error, variables, context)
    },
  })
}
